import { createApp } from '@backstage/frontend-defaults';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import { techDocsMermaidAddonModule } from 'backstage-plugin-techdocs-addon-mermaid';
import visualizerPlugin from '@backstage/plugin-app-visualizer';
import {
  createCatalogEntityKindOverride,
  createUserProfileCardOverride,
} from '@ns/plugin-ns-catalog-entity-ui';
import { TechRadarDetailPage } from '@ns/plugin-ns-tech-radar';
import azurePickersPlugin from '@ns/plugin-azure-pickers';
import entraPickersPlugin from '@ns/plugin-entra-pickers';
import pickerSharedPlugin from '@ns/plugin-picker-shared';
import tessaScaffolderFieldPlugin from '@ns/scaffolder-field-tessa';
import { nsConfigLoader } from './config-loader';
import { appModule } from './modules/app';
import { pageIconModules } from './page-icons';

// Most plugins are loaded via app.packages auto-discovery from
// packages/app/package.json using their Backstage package role and entrypoints.
// Explicit selections and local overrides live in `features`:
//
// - appModule: a local app module, not an npm package. The sidebar comes from
//   @ns/plugin-app-module-navigation through discovery.
// - pageIconModules: the ns-theme glyph of each upstream page in the sidebar,
//   as page icon. See ./page-icons.
// - techDocsMermaidAddonModule: explicit addon selection; its discoverable
//   default export is the same module, which the framework deduplicates.
// - visualizerPlugin: @backstage/plugin-app-visualizer has no `exports` field
//   at all, so auto-discovery can't find a frontend-plugin entry.
//
// Imports for catalog / scaffolder / techdocs / api-docs / catalog-import / org
// stay only for their routes/externalRoutes references in bindRoutes below.
export default createApp({
  // Merge-only dev-parity config loader: in dev (yarn dev) it applies the same per-item
  // app.extensions merge the prod backend applies, so yarn dev matches prod. In prod the
  // already-merged config arrives via the plugin-app-backend script-tag and the loader is
  // idempotent over it. No governance frontend-side (that is the backend's job before
  // injection). See config-loader.ts.
  advanced: { configLoader: nsConfigLoader },
  features: [
    appModule,
    // Gives the upstream pages in the sidebar their ns-theme glyph as page
    // icon. The toolbar and the sidebar both show it. See ./page-icons.
    ...pageIconModules,
    // Overrides the org user-profile card so it also shows the person's
    // Functie (job title) and Afdeling (department) from the entra.ns.nl/*
    // annotations, in addition to the framework avatar/name/email/groups.
    // See plugins/ns-catalog-entity-ui/src/user-profile-card.
    createUserProfileCardOverride(),
    // NFS override of `page:catalog/entity` switching kind-specific detail
    // pages on the framework catalog URL. The shared helper renders a
    // dedicated page component for each registered kind and falls back to
    // the framework default for every other kind. NFS resolution is
    // last-module-wins, so this is the SINGLE catalog-entity-kind override
    // app-wide. See plugins/ns-catalog-entity-ui/src/catalog-entity-kind.
    createCatalogEntityKindOverride(
      [
        { kind: 'TechRadar', page: TechRadarDetailPage },
        // All other kinds use the native entity page, with NS theme styling.
        // Only TechRadar retains its dedicated detail page.
      ],
    ),
    techDocsMermaidAddonModule,
    visualizerPlugin,
    // Registers the Tessa scaffolder picker field (TessaLicenseTypePicker).
    tessaScaffolderFieldPlugin,
    // Registers the shared scaffolder fields: FieldListPicker,
    // DependentChoicePicker and FieldValueLabel reuse values already entered
    // elsewhere in the form, AlertField shows fixed guidance. See
    // plugins/picker-shared/README.md.
    pickerSharedPlugin,
    // Registers the three Azure scaffolder pickers (AzureSubscriptionPicker,
    // AzureResourceGroupPicker, AzureResourcePicker). In development they run
    // against the local ARM stub: filled pickers, no credential, no network.
    // See plugins/arm-proxy/azure-pickers/README.md.
    azurePickersPlugin,
    // Registers the Microsoft Entra ID scaffolder fields. See plugins/entra-pickers/README.md.
    entraPickersPlugin,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});
